import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createModelsApi,
  createWorkbooksApi,
  detectionStreams,
  isModelEnabled,
  tacticKey,
  workbookOrigin,
  workbookRunMode,
  workbookUpdateAvailable,
} from '@bloo-team/api-client'
import type { Detection, MlModel, Workbook, WorkbookOrigin, WorkbookRunMode } from '@bloo-team/api-client'
import { client } from '@bloo-team/core-shell'
import { isDemoSession } from '@bloo-team/core-shell'
import { DEMO_ANALYTICS } from './coverage'

/**
 * Vantage · Detections — the unified detection registry
 * (docs/detections-requirements.md).
 *
 * One record per detection, whatever technology implements it. The spine is
 * attack/analytics — the same 6,543 rows Coverage joins into the matrix,
 * except here deduped BY DETECTION (a detection tagged with three techniques
 * is one row carrying three techniques, not three rows). workbook/list
 * enriches the spine with what analytics does not carry: folder, stage,
 * version, author/origin, lock, timestamps — and contributes rows of its
 * own for content with no MITRE tagging (analytics only enumerates tagged
 * techniques, so untagged content would otherwise be invisible; making that
 * gap VISIBLE is a design goal, not an accident).
 *
 * `kind` is a facet, not a screen: today everything the two endpoints carry
 * is a workbook; ML and behavioral models join the same registry in phase 3
 * as new kinds, and the UI grows a chip, not a route.
 */

export type DetectionKind = 'workbook' | 'ml' | 'behavioral'
export type DetectionSeverity = 'critical' | 'high' | 'medium' | 'low' | 'info'

export interface DetectionRecord {
  id: string
  name: string
  kind: DetectionKind
  origin: WorkbookOrigin
  enabled: boolean
  runMode: WorkbookRunMode
  /** 'dev' | 'beta' | 'prod' | '' — workbook maturity. */
  stage: string
  version: string
  updateAvailable: boolean
  locked: boolean
  isPrivate: boolean
  severity: DetectionSeverity | null
  /** Signal-block score as delivered ("5"); '' when unknown. */
  score: string
  tactic: string
  /** Every technique this detection is tagged with. Empty = untagged. */
  techniques: string[]
  /** Sub-techniques from the signal blocks ("T1595 - Active Scanning"). */
  subtechniques: string[]
  tags: string[]
  /** Folder path, '/' separated. '' = unfiled. */
  folder: string
  streams: string[]
  /** Signals in the analytics window (30d). */
  signalCount: number
  /** False when the source sent no count (paged rows with a null
   * signal_count) — render "—", not 0. */
  signalCountKnown: boolean
  /** ms epoch of the last signal; null = none known. */
  lastFired: number | null
  /** Detection knowledge-base markdown, when the workbook carries one. */
  kb: string
  /** ms epoch; null when unknown. */
  updatedAt: number | null
}

/* ------------------------------------------------------------------- merge */

const SEVERITIES: DetectionSeverity[] = ['critical', 'high', 'medium', 'low', 'info']

export function normalizeSeverity(raw: string): DetectionSeverity | null {
  const s = raw.trim().toLowerCase()
  return (SEVERITIES as string[]).includes(s) ? (s as DetectionSeverity) : null
}

export function baseRecord(id: string, name: string): DetectionRecord {
  return {
    id,
    name,
    kind: 'workbook',
    origin: 'custom',
    enabled: false,
    runMode: 'manual',
    stage: '',
    version: '',
    updateAvailable: false,
    locked: false,
    isPrivate: false,
    severity: null,
    score: '',
    tactic: '',
    techniques: [],
    subtechniques: [],
    tags: [],
    folder: '',
    streams: [],
    signalCount: 0,
    signalCountKnown: true,
    lastFired: null,
    kb: '',
    updatedAt: null,
  }
}

/**
 * analytics + workbook list -> registry. Join key is the id (workbook id =
 * detection id = the signals' $SourceId); analytics rows with no id fall
 * back to the name. Either source alone still produces a usable registry —
 * the workbook list is unverified live and may fail independently.
 */
export function mergeDetections(
  analytics: Record<string, Detection[]>,
  workbooks: Workbook[],
  lastFired?: Record<string, number>,
): DetectionRecord[] {
  const byId = new Map<string, DetectionRecord>()

  for (const [technique, dets] of Object.entries(analytics)) {
    for (const d of dets) {
      const key = d.id || d.name
      const rec = byId.get(key) ?? baseRecord(d.id || d.name, d.name)
      rec.enabled = rec.enabled || (d.active && !d.disabled)
      rec.origin = d.native ? 'native' : rec.origin
      rec.runMode = d.type ? workbookRunMode({ type: d.type }) : rec.runMode
      rec.severity = rec.severity ?? normalizeSeverity(d.severity)
      rec.tactic = rec.tactic || d.tactic
      if (!rec.techniques.includes(technique)) rec.techniques.push(technique)
      rec.streams = [...new Set([...rec.streams, ...detectionStreams(d)])]
      // The same detection repeats under each technique with the same count.
      rec.signalCount = Math.max(rec.signalCount, d.signal_count)
      byId.set(key, rec)
    }
  }

  for (const w of workbooks) {
    const rec = byId.get(w.id) ?? baseRecord(w.id, w.name)
    rec.name = w.name || rec.name
    rec.origin = workbookOrigin(w)
    // The workbook row is the AUTHORITY on enabled: the visibility endpoint
    // flips `disabled` and the list refetches — analytics' `active` must not
    // override it or a toggle would never take effect on screen.
    rec.enabled = !w.disabled
    rec.runMode = w.type ? workbookRunMode(w) : rec.runMode
    rec.stage = w.stage
    rec.version = w.version
    rec.updateAvailable = workbookUpdateAvailable(w)
    rec.locked = w.is_locked
    rec.isPrivate = w.is_private
    rec.tags = w.signal_tags
    rec.folder = w.path.replace(/^\//, '')
    rec.streams = [...new Set([...rec.streams, ...w.stream_flows])]
    rec.signalCount = Math.max(rec.signalCount, w.signal_count)
    rec.lastFired = w.signal_last_seen ?? rec.lastFired
    rec.kb = w.signal_kb
    rec.updatedAt = w.last_modified_tstamp !== null ? w.last_modified_tstamp * 1000 : rec.updatedAt
    byId.set(w.id, rec)
  }

  if (lastFired) {
    for (const rec of byId.values()) {
      const fired = lastFired[rec.name]
      if (fired !== undefined) rec.lastFired = Math.max(rec.lastFired ?? 0, fired)
    }
  }

  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name))
}

/**
 * ML models as registry rows (kind 'ml'). The legacy surface carries only
 * id/name/status — the blank severity/MITRE/folder here is the point: the
 * gap is VISIBLE in the same library instead of hidden on a separate
 * screen (backend ask: technique-tag models).
 */
export function mlDetectionRecords(models: MlModel[]): DetectionRecord[] {
  return models
    .map((m) => {
      const rec = baseRecord(m.model_id, m.model_name || m.model_id)
      rec.kind = 'ml'
      rec.origin = 'custom'
      rec.enabled = isModelEnabled(m)
      rec.folder = 'ML Models'
      return rec
    })
    .sort((a, b) => a.name.localeCompare(b.name))
}

/* ----------------------------------------------------------------- filters */

export interface DetectionLens {
  kind?: DetectionKind
  status?: 'enabled' | 'disabled'
  origin?: WorkbookOrigin
  stage?: string
  tactic?: string
  technique?: string
  folder?: string
  severity?: DetectionSeverity
  stream?: string
  text?: string
}

export function matchesLens(d: DetectionRecord, lens: DetectionLens): boolean {
  if (lens.kind && d.kind !== lens.kind) return false
  if (lens.status && (lens.status === 'enabled') !== d.enabled) return false
  if (lens.origin && d.origin !== lens.origin) return false
  if (lens.stage && d.stage !== lens.stage) return false
  // Tactic matches through the canonical fold — live data carries spelling
  // and case variants of the same tactic.
  if (lens.tactic && tacticKey(d.tactic) !== tacticKey(lens.tactic)) return false
  if (lens.technique && !d.techniques.includes(lens.technique)) return false
  // Folder matches the subtree: picking "Native" keeps "Native/Execution".
  if (lens.folder && d.folder !== lens.folder && !d.folder.startsWith(`${lens.folder}/`)) return false
  if (lens.severity && d.severity !== lens.severity) return false
  if (lens.stream && !d.streams.includes(lens.stream)) return false
  if (lens.text) {
    const t = lens.text.trim().toLowerCase()
    if (t) {
      const hay = `${d.name} ${d.folder} ${d.techniques.join(' ')} ${d.tags.join(' ')}`.toLowerCase()
      if (!hay.includes(t)) return false
    }
  }
  return true
}

export interface DetectionCounts {
  total: number
  enabled: number
  /** Enabled with zero signals in the analytics window — the honest number
   * when no last-fired query has run. */
  quiet: number
  silent: number
  neverFired: number
  updates: number
  untagged: number
}

/** Silent: enabled, has fired knowledge, and nothing inside the window. */
export function countDetections(records: DetectionRecord[], nowMs: number): DetectionCounts {
  const c: DetectionCounts = { total: 0, enabled: 0, quiet: 0, silent: 0, neverFired: 0, updates: 0, untagged: 0 }
  const DAY = 24 * 60 * 60 * 1000
  for (const d of records) {
    c.total++
    if (d.enabled) c.enabled++
    if (d.updateAvailable) c.updates++
    if (!d.techniques.length) c.untagged++
    if (d.enabled && d.signalCount === 0) {
      c.quiet++
      if (d.lastFired === null) c.neverFired++
      else if (nowMs - d.lastFired > 14 * DAY) c.silent++
    }
  }
  return c
}

export type DetectionSortKey =
  | 'name' | 'origin' | 'stage' | 'severity' | 'signals' | 'lastFired' | 'updated' | 'verdicts'
export type SortDir = 'asc' | 'desc'

/**
 * Per-detection verdict summary the registry sorts on (signal-dispositions
 * supplies it; kept structural here so this module stays free of the
 * signals stack).
 */
export interface DetectionVerdicts {
  fpRate: number | null
  closed: number
}

const sevRank = (s: DetectionSeverity | null) => (s === null ? SEVERITIES.length : SEVERITIES.indexOf(s))

/**
 * @param verdicts keyed by record id; 'verdicts' sorts by FP rate then by
 * closed count, with unverdicted rows last in either direction.
 */
export function sortDetections(
  records: DetectionRecord[],
  key: DetectionSortKey,
  dir: SortDir,
  verdicts?: ReadonlyMap<string, DetectionVerdicts>,
): DetectionRecord[] {
  const sign = dir === 'asc' ? 1 : -1
  const verdictRank = (d: DetectionRecord): number | null => {
    const v = verdicts?.get(d.id)
    return v && v.fpRate !== null ? v.fpRate * 1_000_000 + v.closed : null
  }
  const cmp = (a: DetectionRecord, b: DetectionRecord): number => {
    switch (key) {
      case 'name': return a.name.localeCompare(b.name)
      case 'origin': return a.origin.localeCompare(b.origin)
      case 'stage': return a.stage.localeCompare(b.stage)
      case 'severity': return sevRank(a.severity) - sevRank(b.severity)
      case 'signals': return a.signalCount - b.signalCount
      case 'lastFired': return (a.lastFired ?? 0) - (b.lastFired ?? 0)
      case 'updated': return (a.updatedAt ?? 0) - (b.updatedAt ?? 0)
      case 'verdicts': {
        const va = verdictRank(a)
        const vb = verdictRank(b)
        if (va === null && vb === null) return 0
        // Unverdicted rows sink regardless of direction (the caller
        // multiplies by sign, so returning sign lands a after b).
        if (va === null) return sign
        if (vb === null) return -sign
        return va - vb
      }
    }
  }
  return [...records].sort((a, b) => sign * cmp(a, b) || a.name.localeCompare(b.name))
}

/** Distinct non-empty values of a field, for PickList options. */
export function lensOptions(
  records: DetectionRecord[],
  field: 'tactic' | 'stage' | 'stream' | 'technique' | 'folder',
): string[] {
  const out = new Set<string>()
  for (const d of records) {
    if (field === 'stream') d.streams.forEach((s) => out.add(s))
    else if (field === 'technique') d.techniques.forEach((t) => out.add(t))
    else if (field === 'folder') {
      // Offer every ancestor too, so "Native" filters the whole subtree.
      const parts = d.folder.split('/').filter(Boolean)
      for (let i = 1; i <= parts.length; i++) out.add(parts.slice(0, i).join('/'))
    } else if (d[field]) out.add(d[field])
  }
  return [...out].sort((a, b) => a.localeCompare(b))
}

/* ---------------------------------------------------------------- breakdown */

export interface TacticBreakdownRow {
  tactic: string
  enabled: number
  disabled: number
  total: number
}

/**
 * Per-tactic enabled/disabled split — the library's overview spine.
 * Spelling variants fold through tacticKey (live carries "Defence Evasion"
 * beside "Defense Evasion" and case variants). With `order` (the attack
 * skeleton) rows come back in kill-chain order under the skeleton's own
 * spelling, leftovers appended by weight; without it, by weight. Untagged
 * content folds into '(untagged)'.
 */
export function tacticBreakdown(
  records: DetectionRecord[],
  order?: string[],
): TacticBreakdownRow[] {
  const rows = new Map<string, TacticBreakdownRow>()
  const canonical = new Map<string, string>()
  for (const name of order ?? []) {
    if (!canonical.has(tacticKey(name))) canonical.set(tacticKey(name), name)
  }
  for (const d of records) {
    const raw = d.tactic || '(untagged)'
    const key = tacticKey(raw)
    const display = canonical.get(key) ?? rows.get(key)?.tactic ?? raw
    const row = rows.get(key) ?? { tactic: display, enabled: 0, disabled: 0, total: 0 }
    row.total++
    if (d.enabled) row.enabled++
    else row.disabled++
    rows.set(key, row)
  }
  const byWeight = (a: TacticBreakdownRow, b: TacticBreakdownRow) =>
    b.total - a.total || a.tactic.localeCompare(b.tactic)
  if (!order?.length) return [...rows.values()].sort(byWeight)
  const out: TacticBreakdownRow[] = []
  for (const name of order) {
    const row = rows.get(tacticKey(name))
    if (row) {
      out.push(row)
      rows.delete(tacticKey(name))
    }
  }
  return [...out, ...[...rows.values()].sort(byWeight)]
}

/* ------------------------------------------------------------------ pivots */

/** One detection's signals, as plain teachable DQL — the Search pivot. */
export function detectionSignalsQuery(name: string): string {
  return `stream=SIGNALS where detectionname = '${name.replace(/'/g, "''")}' | duration 30d | limit 100`
}

/* --------------------------------------------------------------- demo mode */

/**
 * Demo workbook rows are derived from DEMO_ANALYTICS (same ids — the join
 * must exercise the merge), with folder/stage/version/author assigned the
 * way a real tenant looks: mostly native prod content, some community, a
 * few custom rows in dev/beta, one pending native update.
 */
const DEMO_OVERRIDES: Record<string, Partial<Workbook>> = {
  'login-outside-in': { author: 'gotham-soc', stage: 'prod', path: '/Custom/Geo' },
  'dormant-account-login': { author: 'gotham-soc', stage: 'beta', path: '/Custom/Identity' },
  'cron-entry-from-web-process': { author: 'gotham-soc', stage: 'dev', path: '/Custom/Linux', disabled: true },
  'dns-tunnelling-pattern': { author: 'community', stage: 'prod', path: '/Community/DNS' },
  'long-lived-dns-txt-queries': { author: 'community', stage: 'beta', path: '/Community/DNS' },
  // The pending-update demo: upstream shipped after this copy was installed.
  'suspicious-powershell-execution': { create_tstamp: 1_750_000_000, last_updated_tstamp: 1_755_500_000 },
  'lsass-memory-access': { is_locked: true },
  'mass-file-rename-burst': { disabled: true },
  'upload-to-personal-cloud-storage': { disabled: true },
}

function demoWorkbookFor(d: Detection, technique: string): Workbook {
  const o = DEMO_OVERRIDES[d.id] ?? {}
  return {
    id: d.id,
    name: d.name,
    path: o.path ?? `/Native/${d.tactic || 'Unclassified'}`,
    author: o.author ?? 'DNIF',
    type: d.type,
    stage: o.stage ?? 'prod',
    version: o.version ?? 'v4',
    disabled: o.disabled ?? !d.active,
    is_locked: o.is_locked ?? false,
    is_private: false,
    signal_count: d.signal_count,
    signal_last_seen: null,
    signal_tags: [technique],
    stream_flows: detectionStreams(d),
    signal_kb: '',
    last_modified_tstamp: o.last_updated_tstamp ?? 1_753_000_000,
    create_tstamp: o.create_tstamp ?? 1_740_000_000,
    last_updated_tstamp: o.last_updated_tstamp ?? null,
  }
}

export function demoWorkbooks(): Workbook[] {
  const seen = new Set<string>()
  const out: Workbook[] = []
  for (const [technique, dets] of Object.entries(DEMO_ANALYTICS)) {
    for (const d of dets) {
      if (seen.has(d.id)) continue
      seen.add(d.id)
      out.push(demoWorkbookFor(d, technique))
    }
  }
  return out
}

/** Demo store is mutable so enable/disable is exercisable without a
 * backend; resets on reload. */
let demoWbStore: Workbook[] | null = null
function demoStore(): Workbook[] {
  if (!demoWbStore) demoWbStore = demoWorkbooks()
  return demoWbStore
}

/** The live demo store (mutations included) — the paged demo list reads it. */
export function demoWorkbookStore(): Workbook[] {
  return demoStore()
}

/** Test hook: restore the seed. */
export function resetDemoWorkbooks() {
  demoWbStore = null
}

/** Demo-mode create support (hunts): register a new workbook row. */
export function demoAddWorkbook(w: Workbook) {
  demoStore().push({ ...w })
}

/** Demo ML models — the baseline models behind the EBA ML:baseline story. */
const DEMO_MODELS_SEED: MlModel[] = [
  { model_id: 'ml-login-time-baseline', model_name: 'Login Time Baseline', model_status: 'ENABLED' },
  { model_id: 'ml-outbound-volume-baseline', model_name: 'Outbound Volume Baseline', model_status: 'ENABLED' },
  { model_id: 'ml-process-rarity', model_name: 'Process Rarity', model_status: 'DISABLED' },
]

let demoModelStore: MlModel[] | null = null
function demoModels(): MlModel[] {
  if (!demoModelStore) demoModelStore = DEMO_MODELS_SEED.map((m) => ({ ...m }))
  return demoModelStore
}

export function resetDemoModels() {
  demoModelStore = null
}

export const DEMO_MODEL_YAML = `model: baseline
entity: user
feature: login_hour
window: 30d
sensitivity: medium
emit:
  severity: medium
  detection: Login outside baseline hours
`

/* ------------------------------------------------------------------- hooks */

export const liveWorkbooksApi = createWorkbooksApi(client)
export const liveModelsApi = createModelsApi(client)

const workbooksKey = (q: { clusterId: string; scopeId: string }, isDemo: boolean) =>
  ['workbooks', q.clusterId, q.scopeId, isDemo ? 'demo' : 'live'] as const

/** The stats call's answer: its rows plus `count`, every workbook in scope. */
export interface WorkbooksOverview {
  rows: Workbook[]
  total: number
}

const workbooksOverviewQuery = (q: { clusterId: string; scopeId: string }, isDemo: boolean) => ({
  queryKey: workbooksKey(q, isDemo),
  queryFn: (): Promise<WorkbooksOverview> =>
    isDemo
      ? Promise.resolve({ rows: demoStore().map((w) => ({ ...w })), total: demoStore().length })
      : liveWorkbooksApi.listOverview(q),
  staleTime: 5 * 60_000,
  retry: 1,
})

/** The workbook rows of the stats call (overview joins, the home journey). */
export function useWorkbooks(q: { clusterId: string; scopeId: string }) {
  const isDemo = isDemoSession()
  return useQuery({ ...workbooksOverviewQuery(q, isDemo), select: (d: WorkbooksOverview) => d.rows })
}

/** The same one request with its `count` — the library's chip numbers. */
export function useWorkbooksOverview(q: { clusterId: string; scopeId: string }) {
  const isDemo = isDemoSession()
  return useQuery(workbooksOverviewQuery(q, isDemo))
}

const modelsKey = (q: { clusterId: string; scopeId: string }, isDemo: boolean) =>
  ['ml-models', q.clusterId, q.scopeId, isDemo ? 'demo' : 'live'] as const

/**
 * ML model list. `enabled: false` (tier-gated — enterprise only in legacy)
 * yields an empty list without a request; demo always answers.
 */
export function useModels(q: { clusterId: string; scopeId: string; enabled?: boolean }) {
  const isDemo = isDemoSession()
  const allowed = q.enabled ?? true
  return useQuery<MlModel[]>({
    queryKey: modelsKey(q, isDemo),
    queryFn: () =>
      isDemo ? Promise.resolve(demoModels().map((m) => ({ ...m }))) : liveModelsApi.list(q),
    enabled: allowed,
    staleTime: 5 * 60_000,
    retry: 1,
  })
}

/** The decoded YAML for one model, fetched lazily for the detail panel. */
export function useModelDefinition(q: { clusterId: string; scopeId: string; modelId: string | null }) {
  const isDemo = isDemoSession()
  return useQuery<string>({
    queryKey: ['ml-model-def', q.clusterId, q.scopeId, q.modelId, isDemo ? 'demo' : 'live'],
    queryFn: () =>
      isDemo
        ? Promise.resolve(DEMO_MODEL_YAML)
        : liveModelsApi.definition({ ...q, modelId: q.modelId as string }),
    enabled: q.modelId !== null,
    staleTime: 5 * 60_000,
    retry: 1,
  })
}

/**
 * Enable/disable across kinds: workbooks go through the bulk visibility
 * endpoint; ML models flip one at a time (enable/disable are separate
 * endpoints with no bulk form in legacy).
 */
export function useSetDetectionEnabled(q: { clusterId: string; scopeId: string }) {
  const isDemo = isDemoSession()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { workbookIds: string[]; modelIds: string[]; enabled: boolean }) => {
      if (isDemo) {
        const wbIds = new Set(input.workbookIds)
        demoWbStore = demoStore().map((w) =>
          wbIds.has(w.id) ? { ...w, disabled: !input.enabled } : w,
        )
        const mlIds = new Set(input.modelIds)
        demoModelStore = demoModels().map((m) =>
          mlIds.has(m.model_id)
            ? { ...m, model_status: input.enabled ? 'ENABLED' : 'DISABLED' }
            : m,
        )
        return
      }
      const jobs: Promise<void>[] = []
      if (input.workbookIds.length) {
        jobs.push(
          liveWorkbooksApi.setVisibility({
            ...q,
            ids: input.workbookIds,
            visibility: input.enabled ? 'enable' : 'disable',
          }),
        )
      }
      for (const modelId of input.modelIds) {
        jobs.push(
          input.enabled
            ? liveModelsApi.enable({ ...q, modelId })
            : liveModelsApi.disable({ ...q, modelId }),
        )
      }
      await Promise.all(jobs)
    },
    onSuccess: (_d, input) => {
      if (input.workbookIds.length) void qc.invalidateQueries({ queryKey: workbooksKey(q, isDemo) })
      if (input.modelIds.length) void qc.invalidateQueries({ queryKey: modelsKey(q, isDemo) })
    },
  })
}

/** Split a set of registry ids by kind for the enable/disable mutation. */
export function splitIdsByKind(
  ids: string[],
  records: DetectionRecord[],
): { workbookIds: string[]; modelIds: string[] } {
  const kind = new Map(records.map((r) => [r.id, r.kind]))
  return {
    workbookIds: ids.filter((id) => kind.get(id) !== 'ml'),
    modelIds: ids.filter((id) => kind.get(id) === 'ml'),
  }
}
